import { useRef, useEffect } from "react";
import { X, Trash2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTechniqueChat } from "./TechniqueChatProvider";
import { ChatMessage } from "./ChatMessage";
import { ChatInput } from "./ChatInput";
import { TypingIndicator } from "./TypingIndicator";
import { QuickActions } from "./QuickActions";

export function TechniqueChatPanel() {
  const {
    isOpen,
    closeChat,
    messages,
    isLoading,
    error,
    technique,
    remainingMessages,
    clearHistory,
  } = useTechniqueChat();

  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  if (!technique) return null;

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && closeChat()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md p-0 flex flex-col h-full"
      >
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div>
                <SheetTitle className="text-base">
                  Technique Assistant
                </SheetTitle>
                <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                  {technique.name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={clearHistory}
                  className="h-8 w-8"
                  title="Clear chat"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={closeChat}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* Messages */}
        <ScrollArea ref={scrollRef} className="flex-1 px-4">
          <div className="py-4 space-y-4">
            {/* Welcome message if no messages */}
            {messages.length === 0 && !isLoading && (
              <div className="text-center py-8">
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-violet-100 to-indigo-100 dark:from-violet-900/30 dark:to-indigo-900/30 flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="h-6 w-6 text-violet-600 dark:text-violet-400" />
                </div>
                <h3 className="font-medium mb-2">Hi! I'm here to help</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Ask me anything about the{" "}
                  <span className="font-medium">{technique.name}</span>{" "}
                  technique.
                </p>
                <QuickActions />
              </div>
            )}

            {/* Message list */}
            {messages.map((message, index) => (
              <ChatMessage key={index} message={message} />
            ))}

            {/* Loading indicator */}
            {isLoading && <TypingIndicator />}

            {/* Error message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm p-3 rounded-lg">
                {error}
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Quick actions after conversation starts */}
        {messages.length > 0 && !isLoading && (
          <div className="px-4 py-2 border-t">
            <QuickActions compact />
          </div>
        )}

        {/* Input */}
        <div className="px-4 py-3 border-t flex-shrink-0">
          <ChatInput />
          {remainingMessages !== null && (
            <p className="text-xs text-muted-foreground text-center mt-2">
              {remainingMessages} messages remaining today
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
